import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import {
  ArrowLeft, Type, AlignLeft, Image as ImageIcon, MousePointerClick, Minus, MoveVertical, Columns2,
  Flag, Share2, Code2, PanelBottom, Sparkles, ChevronUp, ChevronDown, Copy, Trash2, Eye, Monitor, Smartphone,
  CheckCircle2, XCircle, Send, CalendarClock, Mail, Youtube, Search, Link2, Clapperboard,
} from 'lucide-react';
import { API, NL_CATEGORIES, cardCls, txtMain, txtSub } from './nlShared';
import { LoadingSpinner } from '../core';
import { NlAiModal, aiImprove } from './NlAi';
import { toast } from 'sonner';

const uid = () => Math.random().toString(36).slice(2, 10);
const goLink = (id) => `${process.env.REACT_APP_BACKEND_URL}/go/${id}`;
export const ytId = (url) => (url || '').match(/(?:v=|youtu\.be\/|shorts\/|embed\/)([\w-]{6,})/)?.[1] || null;

const BLOCK_DEFS = {
  title: { label: 'Titre', icon: Type, defaults: { text: 'Votre titre ici', size: 'md', align: 'left' } },
  text: { label: 'Texte', icon: AlignLeft, defaults: { text: 'Bonjour {{firstname}},\nVotre texte ici…', align: 'left' } },
  image: { label: 'Image', icon: ImageIcon, defaults: { url: '', link: '', alt: '', rounded: true } },
  button: { label: 'Bouton', icon: MousePointerClick, defaults: { text: 'Découvrir', url: '', style: 'solid', shape: 'pill', align: 'center' } },
  divider: { label: 'Séparateur', icon: Minus, defaults: {} },
  spacer: { label: 'Espace', icon: MoveVertical, defaults: { height: 24 } },
  banner: { label: 'Bannière', icon: Flag, defaults: { url: '', link: '' } },
  youtube: { label: 'Vidéo YouTube', icon: Youtube, defaults: { url: '', title: '' } },
  mayacontent: { label: 'Contenu Maya+', icon: Clapperboard, defaults: { content_id: '', title: '', image: '', description: '', cta: 'Regarder maintenant' } },
  columns2: { label: '2 colonnes', icon: Columns2, defaults: { items: [{ image: '', title: 'Titre 1', text: '', url: '' }, { image: '', title: 'Titre 2', text: '', url: '' }] } },
  social: { label: 'Réseaux sociaux', icon: Share2, defaults: { links: { facebook: '', instagram: '', youtube: '', x: '' } } },
  html: { label: 'HTML', icon: Code2, defaults: { code: '' } },
  footer: { label: 'Footer', icon: PanelBottom, defaults: { text: "L'équipe Maya+\nDes histoires qui ont du sens." } },
  dynamic: { label: 'Contenu dynamique', icon: Sparkles, defaults: { kind: 'nouveautes', count: 4, title: '' } },
};
const DYNAMIC_KINDS = [
  { id: 'nouveautes', label: 'Nouveautés de la semaine' },
  { id: 'top10', label: 'Top 10' },
  { id: 'tendances', label: 'Tendances' },
];
const STEPS = ['Informations', 'Destinataires', 'Contenu', 'Vérification', 'Envoi'];

const inputCls = (light) => `w-full text-sm rounded-lg px-3 py-2.5 outline-none border transition-colors ${
  light ? 'bg-white border-gray-200 text-gray-900 focus:border-[#E9641A]' : 'bg-white/5 border-white/10 text-white focus:border-[#E9641A]'}`;
const lblCls = (light) => `block text-xs font-semibold mb-1.5 ${light ? 'text-gray-600' : 'text-white/60'}`;

export function NlEditor({ theme, campaignId, onExit, autoMode = false }) {
  const light = theme === 'light';
  const [camp, setCamp] = useState(null);
  const [step, setStep] = useState(0);
  const [segments, setSegments] = useState([]);
  const [library, setLibrary] = useState([]);
  const [libTab, setLibTab] = useState('blocks');
  const [libQ, setLibQ] = useState('');
  const [dragOverId, setDragOverId] = useState(null);
  const [selBlock, setSelBlock] = useState(null);
  const [preview, setPreview] = useState(null); // 'desktop' | 'mobile'
  const [testEmails, setTestEmails] = useState('');
  const [sendMode, setSendMode] = useState('now');
  const [schedAt, setSchedAt] = useState('');
  const [saving, setSaving] = useState(false);
  const [showAi, setShowAi] = useState(false);
  const saveTimer = useRef(null);

  useEffect(() => {
    axios.get(`${API}/admin/nl/campaigns/${campaignId}`, { withCredentials: true }).then(r => setCamp(r.data)).catch(() => onExit());
    axios.get(`${API}/admin/nl/segments`, { withCredentials: true }).then(r => setSegments(r.data)).catch(() => {});
    axios.get(`${API}/admin/nl/library`, { withCredentials: true }).then(r => setLibrary(r.data)).catch(() => {});
  }, [campaignId, onExit]);

  const persist = useCallback((next) => {
    setSaving(true);
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      const { name, subject, preheader, from_name, from_email, category, segments, blocks, thumb } = next;
      await axios.put(`${API}/admin/nl/campaigns/${campaignId}`, { name, subject, preheader, from_name, from_email, category, segments, blocks, thumb }, { withCredentials: true }).catch(() => {});
      setSaving(false);
    }, 900);
  }, [campaignId]);

  const update = (patch) => setCamp(c => { const next = { ...c, ...patch }; persist(next); return next; });
  const updateBlocks = (blocks) => {
    const firstImg = blocks.map(b => b.props?.url || b.props?.image || b.props?.items?.[0]?.image).find(Boolean) || '';
    update({ blocks, thumb: firstImg });
  };

  if (!camp) return <LoadingSpinner />;
  const blocks = camp.blocks || [];
  const locked = ['sent', 'sending'].includes(camp.status);
  const selSegs = camp.segments || (camp.segment ? [camp.segment] : []);
  const totalContacts = selSegs.reduce((a, s) => a + (segments.find(x => x.id === s.id)?.count || 0), 0);

  const toggleSeg = (s) => {
    const has = selSegs.some(x => x.id === s.id);
    const next = has ? selSegs.filter(x => x.id !== s.id) : [...selSegs, { id: s.id, label: s.label }];
    update({ segments: next });
  };

  const insertBlock = (type, props, at) => {
    const b = { id: uid(), type, props: { ...(BLOCK_DEFS[type]?.defaults || {}), ...(props || {}) } };
    const arr = [...blocks];
    arr.splice(at === undefined ? arr.length : at, 0, b);
    updateBlocks(arr);
    setSelBlock(b.id);
  };
  const addBlock = (type, props) => insertBlock(type, props);

  const handleDrop = (e, targetId) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverId(null);
    if (locked) return;
    const data = e.dataTransfer.getData('text/plain');
    if (!data) return;
    const insertAt = targetId ? blocks.findIndex(b => b.id === targetId) : blocks.length;
    if (data.startsWith('move:')) {
      const id = data.slice(5);
      if (id === targetId) return;
      const from = blocks.findIndex(b => b.id === id);
      if (from < 0) return;
      const arr = [...blocks];
      const [m] = arr.splice(from, 1);
      let to = targetId ? arr.findIndex(b => b.id === targetId) : arr.length;
      if (to < 0) to = arr.length;
      arr.splice(to, 0, m);
      updateBlocks(arr);
    } else if (data.startsWith('new:')) {
      insertBlock(data.slice(4), null, insertAt);
    } else if (data.startsWith('img:')) {
      try { const c = JSON.parse(data.slice(4)); insertBlock('image', { url: c.url, alt: c.title, link: c.link }, insertAt); } catch { /* noop */ }
    } else if (data.startsWith('dyn:')) {
      insertBlock('dynamic', { kind: data.slice(4) }, insertAt);
    }
  };
  const patchBlock = (id, propsPatch) => updateBlocks(blocks.map(b => b.id === id ? { ...b, props: { ...b.props, ...propsPatch } } : b));
  const moveBlock = (id, dir) => {
    const i = blocks.findIndex(b => b.id === id);
    const j = i + dir;
    if (j < 0 || j >= blocks.length) return;
    const arr = [...blocks];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    updateBlocks(arr);
  };
  const dupBlock = (id) => {
    const b = blocks.find(x => x.id === id);
    updateBlocks([...blocks, { ...b, id: uid() }]);
  };
  const delBlock = (id) => { updateBlocks(blocks.filter(b => b.id !== id)); if (selBlock === id) setSelBlock(null); };

  const sendTest = async () => {
    const emails = testEmails.split(',').map(e => e.trim()).filter(Boolean);
    if (!emails.length) return toast.error('Saisissez au moins une adresse');
    try {
      await axios.post(`${API}/admin/nl/campaigns/${campaignId}/test`, { emails }, { withCredentials: true });
      toast.success(`Email test envoyé à ${emails.length} adresse(s)`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Échec de l\'envoi test'); }
  };
  const doSend = async () => {
    try {
      if (sendMode === 'schedule') {
        if (!schedAt) return toast.error('Choisissez une date');
        await axios.post(`${API}/admin/nl/campaigns/${campaignId}/send`, { when: 'schedule', scheduled_at: new Date(schedAt).toISOString() }, { withCredentials: true });
        toast.success('Campagne programmée ✅');
      } else {
        await axios.post(`${API}/admin/nl/campaigns/${campaignId}/send`, { when: 'now' }, { withCredentials: true });
        toast.success('Envoi lancé 🚀');
      }
      onExit();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur d\'envoi'); }
  };

  const seg = { count: totalContacts };
  const words = blocks.map(b => `${b.props?.text || ''} ${b.props?.code || ''}`).join(' ').split(/\s+/).filter(Boolean).length;
  const imgCount = blocks.filter(b => ['image', 'banner'].includes(b.type)).length + blocks.filter(b => b.type === 'columns2').reduce((a, b) => a + (b.props.items || []).filter(i => i.image).length, 0);
  const checklist = [
    { label: 'Objet rempli', ok: !!(camp.subject || '').trim() },
    { label: 'Texte de prévisualisation (pré-header)', ok: !!(camp.preheader || '').trim() },
    { label: 'Au moins un bloc de contenu', ok: blocks.length > 0 },
    { label: 'Un bouton d\'action présent', ok: blocks.some(b => ['button', 'dynamic', 'mayacontent'].includes(b.type)) },
    { label: `Images chargées (${imgCount})`, ok: blocks.filter(b => b.type === 'image' || b.type === 'banner').every(b => !!b.props.url) },
    ...(autoMode ? [] : [{ label: `Destinataires sélectionnés (${selSegs.length} segment${selSegs.length > 1 ? 's' : ''} · ~${totalContacts})`, ok: selSegs.length > 0 && totalContacts > 0 }]),
  ];
  const readMin = Math.max(1, Math.round(words / 200));
  const weightKb = Math.round(JSON.stringify(blocks).length / 102.4) / 10;

  return (
    <div data-testid="nl-editor">
      {/* Header */}
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <button data-testid="nl-editor-back" onClick={onExit} className={`p-2 rounded-lg ${light ? 'hover:bg-gray-100 text-gray-500' : 'hover:bg-white/10 text-white/50'}`}><ArrowLeft size={18} /></button>
        <input data-testid="nl-name-input" value={camp.name} onChange={e => update({ name: e.target.value })} disabled={locked}
          className={`text-lg font-bold bg-transparent outline-none border-b border-transparent focus:border-[#E9641A] ${txtMain(light)} min-w-[220px]`} />
        <span className={`text-[11px] ${txtSub(light)}`}>{saving ? 'Enregistrement…' : 'Enregistré ✓'}</span>
        <div className="flex-1" />
        <button data-testid="nl-ai-btn" onClick={() => setShowAi(true)} disabled={locked}
          className="flex items-center gap-2 px-3 py-2 text-xs font-bold rounded-lg bg-gradient-to-r from-[#7C3AED] to-[#A855F7] text-white hover:opacity-90 disabled:opacity-40">
          <Sparkles size={14} /> Assistant IA
        </button>
        <button data-testid="nl-preview-btn" onClick={() => setPreview('desktop')}
          className={`flex items-center gap-2 px-3 py-2 text-xs font-semibold rounded-lg ${light ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' : 'bg-white/8 text-white hover:bg-white/15'}`}>
          <Eye size={14} /> Aperçu
        </button>
      </div>

      {/* Steps */}
      <div className="flex items-center gap-1 mb-6 flex-wrap">
        {(autoMode ? [[0, 'Informations'], [2, 'Contenu'], [3, 'Vérification'], [4, 'Test']] : STEPS.map((s, i) => [i, s])).map(([i, s], pos, arr) => (
          <React.Fragment key={s}>
            <button data-testid={`nl-step-${i}`} onClick={() => setStep(i)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${
                step === i ? 'bg-[#E9641A] text-white' : i < step ? 'bg-[#E9641A]/15 text-[#E9641A]' : light ? 'bg-gray-100 text-gray-400' : 'bg-white/5 text-white/35'}`}>
              <span className="w-4 h-4 rounded-full bg-black/15 flex items-center justify-center text-[10px]">{pos + 1}</span>{s}
            </button>
            {pos < arr.length - 1 && <span className={`w-4 h-px ${light ? 'bg-gray-200' : 'bg-white/10'}`} />}
          </React.Fragment>
        ))}
      </div>

      {/* STEP 1 — Infos */}
      {step === 0 && (
        <div className={`${cardCls(light)} p-6 max-w-2xl space-y-4`} data-testid="nl-step-infos">
          <div><label className={lblCls(light)}>Nom interne</label>
            <input className={inputCls(light)} value={camp.name} onChange={e => update({ name: e.target.value })} data-testid="nl-info-name" /></div>
          <div><label className={lblCls(light)}>Objet de l'email</label>
            <input className={inputCls(light)} value={camp.subject} onChange={e => update({ subject: e.target.value })} placeholder="🎬 À voir sur MAYA+" data-testid="nl-info-subject" /></div>
          <div><label className={lblCls(light)}>Texte de prévisualisation (pré-header)</label>
            <input className={inputCls(light)} value={camp.preheader} onChange={e => update({ preheader: e.target.value })} placeholder="Découvrez les nouveautés de la semaine…" data-testid="nl-info-preheader" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className={lblCls(light)}>Nom expéditeur</label>
              <input className={inputCls(light)} value={camp.from_name} onChange={e => update({ from_name: e.target.value })} data-testid="nl-info-fromname" /></div>
            <div><label className={lblCls(light)}>Adresse email</label>
              <input className={inputCls(light)} value={camp.from_email} onChange={e => update({ from_email: e.target.value })} data-testid="nl-info-fromemail" /></div>
          </div>
          <div><label className={lblCls(light)}>Catégorie</label>
            <div className="flex flex-wrap gap-2">
              {NL_CATEGORIES.map(cat => (
                <button key={cat} data-testid={`nl-cat-${cat}`} onClick={() => update({ category: cat })}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${camp.category === cat ? 'bg-[#E9641A] text-white' : light ? 'bg-gray-100 text-gray-500' : 'bg-white/5 text-white/50 hover:text-white'}`}>
                  {cat}
                </button>
              ))}
            </div>
          </div>
          <div className="pt-2"><button onClick={() => setStep(autoMode ? 2 : 1)} data-testid="nl-next-1" className="px-5 py-2.5 rounded-lg bg-[#E9641A] hover:bg-[#FE975E] text-white text-sm font-bold">{autoMode ? 'Continuer → Contenu' : 'Continuer → Destinataires'}</button></div>
        </div>
      )}

      {/* STEP 2 — Destinataires (multi-sélection) */}
      {step === 1 && (
        <div data-testid="nl-step-segments">
          <p className={`text-xs mb-3 ${txtSub(light)}`}>Sélectionnez un ou plusieurs segments — les doublons entre segments sont automatiquement fusionnés à l'envoi.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-w-4xl">
            {segments.map(s => {
              const on = selSegs.some(x => x.id === s.id);
              return (
              <button key={s.id} data-testid={`nl-seg-${s.id}`} onClick={() => toggleSeg(s)}
                className={`${cardCls(light)} p-4 text-left transition-all ${on ? '!border-[#E9641A] ring-1 ring-[#E9641A]' : 'hover:border-[#E9641A]/40'}`}>
                <div className="flex items-center justify-between mb-1">
                  <p className={`text-sm font-bold ${txtMain(light)}`}>{s.label}</p>
                  <span className={`w-5 h-5 rounded-md border flex items-center justify-center flex-none ${on ? 'bg-[#E9641A] border-[#E9641A]' : light ? 'border-gray-300' : 'border-white/20'}`}>
                    {on && <CheckCircle2 size={13} className="text-white" />}
                  </span>
                </div>
                <p className={`text-xs ${txtSub(light)}`}>{s.desc}</p>
                <p className="text-xs font-bold text-[#E9641A] mt-2">{s.count.toLocaleString('fr-FR')} contact{s.count > 1 ? 's' : ''}</p>
              </button>
              );
            })}
          </div>
          <div className="flex items-center gap-4 mt-5">
            <button onClick={() => setStep(2)} data-testid="nl-next-2" className="px-5 py-2.5 rounded-lg bg-[#E9641A] hover:bg-[#FE975E] text-white text-sm font-bold">Continuer → Contenu</button>
            <p className={`text-xs ${txtSub(light)}`} data-testid="nl-seg-total">{selSegs.length} segment{selSegs.length > 1 ? 's' : ''} sélectionné{selSegs.length > 1 ? 's' : ''} · ~{totalContacts.toLocaleString('fr-FR')} contacts (hors doublons)</p>
          </div>
        </div>
      )}

      {/* STEP 3 — Éditeur */}
      {step === 2 && (
        <div className="grid grid-cols-12 gap-4" data-testid="nl-step-editor">
          {/* Left: blocks / library */}
          <div className={`col-span-12 lg:col-span-3 ${cardCls(light)} p-3 max-h-[70vh] overflow-y-auto`}>
            <div className={`flex rounded-lg p-0.5 mb-3 ${light ? 'bg-gray-100' : 'bg-white/5'}`}>
              {[['blocks', 'Blocs'], ['maya', 'Maya+'], ['dyn', 'Dynamique']].map(([id, lbl]) => (
                <button key={id} data-testid={`nl-libtab-${id}`} onClick={() => setLibTab(id)}
                  className={`flex-1 px-2 py-1.5 text-[11px] font-bold rounded-md ${libTab === id ? 'bg-[#E9641A] text-white' : light ? 'text-gray-500' : 'text-white/40'}`}>{lbl}</button>
              ))}
            </div>
            {libTab === 'blocks' && (
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(BLOCK_DEFS).filter(([t]) => t !== 'dynamic').map(([type, def]) => (
                  <button key={type} data-testid={`nl-add-${type}`} onClick={() => addBlock(type)} disabled={locked}
                    draggable={!locked} onDragStart={e => e.dataTransfer.setData('text/plain', `new:${type}`)}
                    className={`flex flex-col items-center gap-1.5 p-3 rounded-lg border text-[11px] font-semibold transition-colors cursor-grab active:cursor-grabbing ${
                      light ? 'border-gray-200 text-gray-600 hover:border-[#E9641A] hover:text-[#E9641A]' : 'border-white/10 text-white/60 hover:border-[#E9641A] hover:text-[#E9641A]'}`}>
                    <def.icon size={17} />{def.label}
                  </button>
                ))}
              </div>
            )}
            {libTab === 'maya' && (
              <div>
                <div className={`flex items-center gap-2 px-2.5 py-2 rounded-lg mb-2 ${light ? 'bg-gray-100' : 'bg-white/5'}`}>
                  <Search size={13} className={txtSub(light)} />
                  <input data-testid="nl-lib-search" value={libQ} onChange={e => setLibQ(e.target.value)} placeholder="Rechercher un contenu…"
                    className={`bg-transparent text-xs outline-none flex-1 ${txtMain(light)} placeholder:opacity-40`} />
                </div>
                <p className={`text-[11px] mb-2 ${txtSub(light)}`}>Cliquez ou glissez une affiche dans l'aperçu — lien intelligent inclus</p>
                <div className="grid grid-cols-2 gap-2">
                  {library.filter(c => !libQ.trim() || (c.title || '').toLowerCase().includes(libQ.toLowerCase())).map(c => (
                    <button key={c.id} data-testid={`nl-lib-${c.id}`} disabled={locked}
                      draggable={!locked}
                      onDragStart={e => e.dataTransfer.setData('text/plain', `img:${JSON.stringify({ url: c.banner_url || c.thumbnail_url || c.poster_url, title: c.title, link: goLink(c.id) })}`)}
                      onClick={() => addBlock('image', { url: c.banner_url || c.thumbnail_url || c.poster_url, alt: c.title, link: goLink(c.id) })}
                      className="rounded-lg overflow-hidden border border-transparent hover:border-[#E9641A] transition-colors cursor-grab active:cursor-grabbing">
                      <img src={c.thumbnail_url || c.banner_url || c.poster_url} alt={c.title} className="w-full aspect-video object-cover" loading="lazy" />
                      <p className={`text-[10px] p-1 truncate ${txtSub(light)}`}>{c.title}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {libTab === 'dyn' && (
              <div className="space-y-2">
                <p className={`text-[11px] mb-1 ${txtSub(light)}`}>Blocs intelligents : le contenu est récupéré automatiquement à l'envoi</p>
                {DYNAMIC_KINDS.map(k => (
                  <button key={k.id} data-testid={`nl-dyn-${k.id}`} onClick={() => addBlock('dynamic', { kind: k.id })} disabled={locked}
                    draggable={!locked} onDragStart={e => e.dataTransfer.setData('text/plain', `dyn:${k.id}`)}
                    className={`w-full flex items-center gap-2 p-3 rounded-lg border text-xs font-semibold cursor-grab active:cursor-grabbing ${
                      light ? 'border-gray-200 text-gray-600 hover:border-[#E9641A]' : 'border-white/10 text-white/60 hover:border-[#E9641A]'}`}>
                    <Sparkles size={14} className="text-[#E9641A]" />{k.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Canvas */}
          <div className="col-span-12 lg:col-span-5">
            <div className="rounded-xl p-5 max-h-[70vh] overflow-y-auto" style={{ background: '#0b0612' }} data-testid="nl-canvas"
              onDragOver={e => e.preventDefault()} onDrop={e => handleDrop(e, null)}>
              <img src="https://customer-assets.emergentagent.com/job_cca8665b-5293-4dd5-89e0-2957c9d7e36c/artifacts/zjtkx4ss_LOGO-MAYAPLUS.png" alt="Maya+" className="mx-auto mb-4" style={{ width: 150 }} />
              {blocks.length === 0 && <p className="text-center text-white/30 text-sm py-16">Glissez-déposez ou cliquez sur des blocs depuis le panneau de gauche</p>}
              {blocks.map(b => (
                <div key={b.id} data-testid={`nl-block-${b.id}`} onClick={() => setSelBlock(b.id)}
                  draggable={!locked}
                  onDragStart={e => { e.dataTransfer.setData('text/plain', `move:${b.id}`); }}
                  onDragOver={e => { e.preventDefault(); setDragOverId(b.id); }}
                  onDragLeave={() => setDragOverId(d => (d === b.id ? null : d))}
                  onDrop={e => handleDrop(e, b.id)}
                  className={`relative group rounded-lg mb-1 cursor-grab active:cursor-grabbing border transition-colors ${
                    selBlock === b.id ? 'border-[#E9641A]' : 'border-transparent hover:border-white/20'} ${dragOverId === b.id ? 'border-t-2 !border-t-[#E9641A]' : ''}`}>
                  <div className={`absolute -top-3 right-1 z-10 hidden ${selBlock === b.id ? '!flex' : 'group-hover:flex'} gap-0.5 bg-[#1a0e2e] border border-white/15 rounded-md p-0.5`}>
                    <button onClick={e => { e.stopPropagation(); moveBlock(b.id, -1); }} className="p-1 text-white/50 hover:text-white"><ChevronUp size={12} /></button>
                    <button onClick={e => { e.stopPropagation(); moveBlock(b.id, 1); }} className="p-1 text-white/50 hover:text-white"><ChevronDown size={12} /></button>
                    <button onClick={e => { e.stopPropagation(); dupBlock(b.id); }} className="p-1 text-white/50 hover:text-white"><Copy size={12} /></button>
                    <button onClick={e => { e.stopPropagation(); delBlock(b.id); }} className="p-1 text-white/50 hover:text-red-400"><Trash2 size={12} /></button>
                  </div>
                  <BlockPreview b={b} />
                </div>
              ))}
            </div>
          </div>

          {/* Right: block settings */}
          <div className={`col-span-12 lg:col-span-4 ${cardCls(light)} p-4 max-h-[70vh] overflow-y-auto`} data-testid="nl-block-settings">
            {!selBlock ? <p className={`text-sm py-10 text-center ${txtSub(light)}`}>Sélectionnez un bloc pour l'éditer</p>
              : <BlockSettings b={blocks.find(x => x.id === selBlock)} patch={p => patchBlock(selBlock, p)} light={light} library={library} />}
            <div className="mt-4 pt-4 border-t border-white/10">
              <button onClick={() => setStep(3)} data-testid="nl-next-3" className="w-full px-5 py-2.5 rounded-lg bg-[#E9641A] hover:bg-[#FE975E] text-white text-sm font-bold">Continuer → Vérification</button>
            </div>
          </div>
        </div>
      )}

      {/* STEP 4 — Vérification */}
      {step === 3 && (
        <div className={`${cardCls(light)} p-6 max-w-xl`} data-testid="nl-step-check">
          <p className={`text-sm font-bold mb-4 ${txtMain(light)}`}>Checklist avant envoi</p>
          <div className="space-y-2.5 mb-5">
            {checklist.map((c, i) => (
              <div key={i} className="flex items-center gap-2.5" data-testid={`nl-check-${i}`}>
                {c.ok ? <CheckCircle2 size={17} className="text-emerald-500" /> : <XCircle size={17} className="text-red-400" />}
                <span className={`text-sm ${c.ok ? txtMain(light) : 'text-red-400'}`}>{c.label}</span>
              </div>
            ))}
          </div>
          <div className={`grid grid-cols-3 gap-3 text-center rounded-lg p-4 ${light ? 'bg-gray-50' : 'bg-white/3'}`}>
            <div><p className={`text-lg font-bold ${txtMain(light)}`}>~{readMin} min</p><p className={`text-[10px] uppercase ${txtSub(light)}`}>Lecture</p></div>
            <div><p className={`text-lg font-bold ${txtMain(light)}`}>{weightKb} Ko</p><p className={`text-[10px] uppercase ${txtSub(light)}`}>Poids contenu</p></div>
            <div><p className={`text-lg font-bold ${txtMain(light)}`}>{(seg?.count || 0).toLocaleString('fr-FR')}</p><p className={`text-[10px] uppercase ${txtSub(light)}`}>Destinataires</p></div>
          </div>
          <button onClick={() => setStep(4)} data-testid="nl-next-4" className="mt-5 px-5 py-2.5 rounded-lg bg-[#E9641A] hover:bg-[#FE975E] text-white text-sm font-bold">{autoMode ? 'Continuer → Test' : 'Continuer → Envoi'}</button>
        </div>
      )}

      {/* STEP 5 — Envoi */}
      {step === 4 && (
        <div className="max-w-xl space-y-4" data-testid="nl-step-send">
          <div className={`${cardCls(light)} p-5`}>
            <p className={`text-sm font-bold mb-3 ${txtMain(light)}`}><Mail size={14} className="inline mr-1.5 text-[#E9641A]" />Envoyer un test</p>
            <div className="flex gap-2">
              <input className={inputCls(light)} placeholder="email1@ex.fr, email2@ex.fr" value={testEmails} onChange={e => setTestEmails(e.target.value)} data-testid="nl-test-emails" />
              <button onClick={sendTest} data-testid="nl-test-send" className={`px-4 py-2 rounded-lg text-xs font-bold whitespace-nowrap ${light ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' : 'bg-white/8 text-white hover:bg-white/15'}`}>Envoyer le test</button>
            </div>
          </div>
          {autoMode && (
            <div className={`${cardCls(light)} p-5`}>
              <p className={`text-sm ${txtSub(light)}`}>✅ Cet email est envoyé automatiquement par le scénario — enregistrez vos modifications et revenez à l'onglet Automatisations pour l'activer.</p>
              <button onClick={onExit} data-testid="nl-auto-done" className="mt-4 px-5 py-2.5 rounded-lg bg-[#E9641A] hover:bg-[#FE975E] text-white text-sm font-bold">Terminer</button>
            </div>
          )}
          {!autoMode && (
          <div className={`${cardCls(light)} p-5`}>
            <div className="flex gap-3 mb-4">
              <button data-testid="nl-mode-now" onClick={() => setSendMode('now')}
                className={`flex-1 flex items-center gap-2 justify-center px-4 py-3 rounded-lg text-sm font-bold border transition-colors ${sendMode === 'now' ? 'border-[#E9641A] bg-[#E9641A]/10 text-[#E9641A]' : light ? 'border-gray-200 text-gray-500' : 'border-white/10 text-white/50'}`}>
                <Send size={15} /> Envoyer maintenant
              </button>
              <button data-testid="nl-mode-schedule" onClick={() => setSendMode('schedule')}
                className={`flex-1 flex items-center gap-2 justify-center px-4 py-3 rounded-lg text-sm font-bold border transition-colors ${sendMode === 'schedule' ? 'border-[#E9641A] bg-[#E9641A]/10 text-[#E9641A]' : light ? 'border-gray-200 text-gray-500' : 'border-white/10 text-white/50'}`}>
                <CalendarClock size={15} /> Programmer
              </button>
            </div>
            {sendMode === 'schedule' && (
              <input type="datetime-local" className={inputCls(light)} value={schedAt} onChange={e => setSchedAt(e.target.value)} data-testid="nl-sched-input" />
            )}
            <p className={`text-xs mt-3 ${txtSub(light)}`}>Segments : <b className="text-[#E9641A]">{selSegs.map(s => s.label).join(' + ') || '—'}</b> · ~{totalContacts.toLocaleString('fr-FR')} destinataires · Objet : {camp.subject || '—'}</p>
            <button onClick={doSend} disabled={locked} data-testid="nl-send-btn"
              className="mt-4 w-full px-5 py-3 rounded-lg bg-[#E9641A] hover:bg-[#FE975E] text-white text-sm font-bold disabled:opacity-40">
              {sendMode === 'schedule' ? '📅 Programmer la campagne' : '🚀 Envoyer la campagne'}
            </button>
          </div>
          )}
        </div>
      )}

      {showAi && (
        <NlAiModal light={light} camp={camp} onClose={() => setShowAi(false)}
          onApplySubject={(s) => update({ subject: s.subject, preheader: s.preheader })}
          onApplyBlocks={(full) => {
            const gen = (full.blocks || []).map(b => ({ ...b, id: b.id || uid() }));
            const patch = { blocks: [...blocks, ...gen] };
            if (!camp.subject && full.subject) { patch.subject = full.subject; patch.preheader = camp.preheader || full.preheader; }
            update(patch);
            setStep(2);
          }} />
      )}

      {/* Preview modal */}
      {preview && (
        <div className="fixed inset-0 z-50 bg-black/75 flex items-center justify-center p-4" onClick={() => setPreview(null)} data-testid="nl-preview-modal">
          <div className={`rounded-xl overflow-hidden flex flex-col max-h-[92vh] ${light ? 'bg-white' : 'bg-[#1a0e2e] border border-white/10'}`}
            style={{ width: preview === 'mobile' ? 400 : 680 }} onClick={e => e.stopPropagation()}>
            <div className={`flex items-center justify-between px-4 py-2.5 border-b ${light ? 'border-gray-200' : 'border-white/10'}`}>
              <p className={`text-sm font-bold ${txtMain(light)}`}>Aperçu — {camp.subject || camp.name}</p>
              <div className="flex items-center gap-1">
                <button data-testid="nl-preview-desktop" onClick={() => setPreview('desktop')} className={`p-1.5 rounded ${preview === 'desktop' ? 'bg-[#E9641A] text-white' : txtSub(light)}`}><Monitor size={15} /></button>
                <button data-testid="nl-preview-mobile" onClick={() => setPreview('mobile')} className={`p-1.5 rounded ${preview === 'mobile' ? 'bg-[#E9641A] text-white' : txtSub(light)}`}><Smartphone size={15} /></button>
                <button onClick={() => setPreview(null)} data-testid="nl-preview-close" className={`ml-2 p-1.5 ${txtSub(light)} hover:text-red-400`}><XCircle size={16} /></button>
              </div>
            </div>
            <iframe title="Aperçu newsletter" src={`${API}/admin/nl/campaigns/${campaignId}/preview`} className="w-full flex-1 min-h-[500px]" style={{ background: '#0b0612' }} />
          </div>
        </div>
      )}
    </div>
  );
}

/* Approximate visual rendering of a block inside the canvas */
function BlockPreview({ b }) {
  const p = b.props || {};
  const s = { color: '#fff' };
  switch (b.type) {
    case 'title': return <h3 className="px-2 py-1.5 font-extrabold" style={{ ...s, textAlign: p.align, fontSize: { sm: 16, md: 20, lg: 26 }[p.size] || 20 }}>{p.text}</h3>;
    case 'text': return <p className="px-2 py-1.5 text-[13px] leading-relaxed whitespace-pre-line" style={{ color: 'rgba(255,255,255,0.8)', textAlign: p.align }}>{p.text}</p>;
    case 'image': return p.url ? <img src={p.url} alt="" className="w-full rounded-lg my-1" /> : <div className="my-1 h-24 rounded-lg bg-white/5 flex items-center justify-center text-white/30 text-xs">Image — ajoutez une URL ou choisissez dans Maya+</div>;
    case 'banner': return p.url ? <img src={p.url} alt="" className="w-full rounded-xl my-1" /> : <div className="my-1 h-20 rounded-xl bg-white/5 flex items-center justify-center text-white/30 text-xs">Bannière</div>;
    case 'youtube': {
      const vid = ytId(p.url);
      return vid ? (
        <div className="relative my-1">
          <img src={`https://img.youtube.com/vi/${vid}/hqdefault.jpg`} alt="" className="w-full rounded-xl" />
          <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-14 h-10 bg-[#FF0000] rounded-lg flex items-center justify-center text-white text-lg">▶</span>
          {p.title && <p className="text-xs text-white/70 mt-1">{p.title}</p>}
        </div>
      ) : <div className="my-1 h-24 rounded-xl bg-white/5 flex items-center justify-center text-white/30 text-xs">Vidéo YouTube — collez l'URL de la vidéo</div>;
    }
    case 'mayacontent': return p.image ? (
      <div className="my-1 rounded-xl overflow-hidden border border-white/10 bg-white/[0.03]">
        <img src={p.image} alt="" className="w-full aspect-video object-cover" />
        <div className="p-3">
          <p className="text-sm font-bold text-white">{p.title}</p>
          {p.description && <p className="text-[11px] text-white/50 mt-1 line-clamp-2">{p.description}</p>}
          <span className="inline-block mt-2 px-4 py-1.5 rounded-full text-[11px] font-bold" style={{ background: '#E9641A', color: '#fff' }}>{p.cta || 'Regarder maintenant'}</span>
        </div>
      </div>
    ) : <div className="my-1 h-24 rounded-xl bg-white/5 flex items-center justify-center text-white/30 text-xs px-4 text-center">Contenu Maya+ — recherchez un titre dans le panneau de droite</div>;
    case 'button': return <div className="py-2" style={{ textAlign: p.align }}><span className={`inline-block px-6 py-2.5 text-[13px] font-bold ${p.shape === 'pill' ? 'rounded-full' : 'rounded-lg'}`} style={p.style === 'outline' ? { border: '2px solid #E9641A', color: '#E9641A' } : p.style === 'ghost' ? { color: '#FE975E' } : { background: '#E9641A', color: '#fff' }}>{p.text}</span></div>;
    case 'divider': return <hr className="my-3 border-white/15" />;
    case 'spacer': return <div style={{ height: p.height || 24 }} className="bg-white/[0.02] rounded" />;
    case 'columns2': return <div className="grid grid-cols-2 gap-2 py-1">{(p.items || []).map((it, i) => (<div key={i}>{it.image ? <img src={it.image} alt="" className="w-full rounded-md aspect-video object-cover" /> : <div className="aspect-video bg-white/5 rounded-md" />}<p className="text-xs font-bold mt-1" style={s}>{it.title}</p><p className="text-[10px] text-white/50">{it.text}</p></div>))}</div>;
    case 'social': return <p className="text-center py-2 text-xs font-bold" style={{ color: '#FE975E' }}>{Object.keys(p.links || {}).filter(k => p.links[k]).map(k => k.charAt(0).toUpperCase() + k.slice(1)).join(' · ') || 'Réseaux sociaux (ajoutez vos liens)'}</p>;
    case 'html': return <pre className="text-[10px] text-white/40 bg-white/5 rounded p-2 overflow-hidden max-h-20">{p.code || '<HTML personnalisé>'}</pre>;
    case 'footer': return <p className="text-center text-[11px] text-white/40 py-2 whitespace-pre-line">{p.text}</p>;
    case 'dynamic': return <div className="my-1 rounded-lg border border-dashed border-[#E9641A]/50 bg-[#E9641A]/5 p-4 text-center"><p className="text-xs font-bold text-[#E9641A]">✨ Contenu dynamique : {DYNAMIC_KINDS.find(k => k.id === p.kind)?.label}</p><p className="text-[10px] text-white/40 mt-1">{p.count || 4} contenus insérés automatiquement à l'envoi</p></div>;
    default: return null;
  }
}

/* Settings editor for the selected block */
function BlockSettings({ b, patch, light, library = [] }) {
  const [linkQ, setLinkQ] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  const improveText = async () => {
    setAiBusy(true);
    try { patch({ text: await aiImprove(b.props?.text || '') }); toast.success('Texte réécrit ✨'); }
    catch { toast.error('Erreur de l\'assistant IA'); }
    setAiBusy(false);
  };
  if (!b) return null;
  const p = b.props || {};
  const inp = inputCls(light);
  const lbl = lblCls(light);
  const def = BLOCK_DEFS[b.type];
  const Field = ({ label, children }) => <div className="mb-3"><label className={lbl}>{label}</label>{children}</div>;
  const Pills = ({ value, options, onPick }) => (
    <div className="flex gap-1.5 flex-wrap">{options.map(([v, l]) => (
      <button key={v} onClick={() => onPick(v)} className={`px-2.5 py-1 rounded-full text-[11px] font-bold ${value === v ? 'bg-[#E9641A] text-white' : light ? 'bg-gray-100 text-gray-500' : 'bg-white/8 text-white/50'}`}>{l}</button>
    ))}</div>
  );
  const results = linkQ.trim() ? library.filter(c => (c.title || '').toLowerCase().includes(linkQ.toLowerCase())).slice(0, 6) : [];
  const LinkPicker = ({ onPick }) => (
    <div className="mb-3">
      <label className={lbl}><Link2 size={11} className="inline mr-1" />Lier à un contenu Maya+ (recherche)</label>
      <input className={inp} data-testid="nl-link-search" value={linkQ} onChange={e => setLinkQ(e.target.value)} placeholder="Titre du film / série…" />
      {results.length > 0 && (
        <div className={`mt-1 rounded-lg border overflow-hidden ${light ? 'border-gray-200 bg-white' : 'border-white/10 bg-[#160a26]'}`}>
          {results.map(c => (
            <button key={c.id} data-testid={`nl-link-pick-${c.id}`} onClick={() => { onPick(c); setLinkQ(''); }}
              className={`w-full flex items-center gap-2 px-2 py-1.5 text-left text-xs ${light ? 'hover:bg-gray-50 text-gray-700' : 'hover:bg-white/5 text-white/80'}`}>
              <img src={c.thumbnail_url || c.banner_url || c.poster_url} alt="" className="w-9 h-5.5 rounded object-cover" style={{ height: 22 }} />
              <span className="truncate">{c.title}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
  return (
    <div>
      <p className={`text-sm font-bold mb-3 flex items-center gap-2 ${txtMain(light)}`}><def.icon size={15} className="text-[#E9641A]" />{def.label}</p>
      {b.type === 'title' && (<><Field label="Texte"><input className={inp} value={p.text} onChange={e => patch({ text: e.target.value })} /></Field>
        <Field label="Taille"><Pills value={p.size} options={[['sm', 'Petit'], ['md', 'Moyen'], ['lg', 'Grand']]} onPick={v => patch({ size: v })} /></Field>
        <Field label="Alignement"><Pills value={p.align} options={[['left', 'Gauche'], ['center', 'Centre'], ['right', 'Droite']]} onPick={v => patch({ align: v })} /></Field></>)}
      {b.type === 'text' && (<><Field label="Texte (variables : {{firstname}}, {{email}})"><textarea rows={6} className={inp} value={p.text} onChange={e => patch({ text: e.target.value })} /></Field>
        <button onClick={improveText} disabled={aiBusy} data-testid="nl-ai-improve"
          className="mb-3 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold bg-gradient-to-r from-[#7C3AED] to-[#A855F7] text-white hover:opacity-90 disabled:opacity-50">
          <Sparkles size={12} /> {aiBusy ? 'Réécriture…' : 'Réécrire avec l\'IA'}
        </button>
        <Field label="Alignement"><Pills value={p.align} options={[['left', 'Gauche'], ['center', 'Centre']]} onPick={v => patch({ align: v })} /></Field></>)}
      {(b.type === 'image' || b.type === 'banner') && (<><Field label="URL de l'image"><input className={inp} value={p.url} onChange={e => patch({ url: e.target.value })} /></Field>
        <Field label="Lien au clic"><input className={inp} value={p.link} onChange={e => patch({ link: e.target.value })} placeholder="https://…" /></Field>
        <LinkPicker onPick={c => patch({ link: goLink(c.id), alt: p.alt || c.title })} /></>)}
      {b.type === 'youtube' && (<><Field label="URL de la vidéo YouTube"><input className={inp} data-testid="nl-yt-url" value={p.url} onChange={e => patch({ url: e.target.value })} placeholder="https://www.youtube.com/watch?v=…" /></Field>
        <Field label="Légende (optionnel)"><input className={inp} value={p.title} onChange={e => patch({ title: e.target.value })} /></Field>
        <p className={`text-[11px] ${light ? 'text-gray-400' : 'text-white/35'}`}>La miniature officielle de la vidéo est insérée automatiquement, cliquable vers YouTube.</p></>)}
      {b.type === 'mayacontent' && (<>
        <div className="mb-3">
          <label className={lbl}><Search size={11} className="inline mr-1" />Rechercher un contenu Maya+</label>
          <input className={inp} data-testid="nl-maya-search" value={linkQ} onChange={e => setLinkQ(e.target.value)} placeholder="Titre du film / série…" />
          {results.length > 0 && (
            <div className={`mt-1 rounded-lg border overflow-hidden ${light ? 'border-gray-200 bg-white' : 'border-white/10 bg-[#160a26]'}`}>
              {results.map(c => (
                <button key={c.id} data-testid={`nl-maya-pick-${c.id}`}
                  onClick={() => { patch({ content_id: c.id, title: c.title, image: c.banner_url || c.thumbnail_url || c.poster_url, description: (c.description || '').slice(0, 180) }); setLinkQ(''); }}
                  className={`w-full flex items-center gap-2 px-2 py-1.5 text-left text-xs ${light ? 'hover:bg-gray-50 text-gray-700' : 'hover:bg-white/5 text-white/80'}`}>
                  <img src={c.thumbnail_url || c.banner_url || c.poster_url} alt="" className="w-9 rounded object-cover" style={{ height: 22 }} />
                  <span className="truncate">{c.title}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        {p.content_id && (<>
          <Field label="Titre"><input className={inp} value={p.title} onChange={e => patch({ title: e.target.value })} /></Field>
          <Field label="Description (optionnel)"><textarea rows={3} className={inp} value={p.description} onChange={e => patch({ description: e.target.value })} /></Field>
          <Field label="Texte du bouton"><input className={inp} value={p.cta} onChange={e => patch({ cta: e.target.value })} /></Field>
          <p className={`text-[11px] ${light ? 'text-gray-400' : 'text-white/35'}`}>La carte entière est cliquable et redirige vers la fiche du contenu sur Maya+.</p>
        </>)}
      </>)}
      {b.type === 'button' && (<><Field label="Texte"><input className={inp} value={p.text} onChange={e => patch({ text: e.target.value })} /></Field>
        <Field label="Lien"><input className={inp} value={p.url} onChange={e => patch({ url: e.target.value })} placeholder="https://…" /></Field>
        <LinkPicker onPick={c => patch({ url: goLink(c.id) })} />
        <Field label="Style"><Pills value={p.style} options={[['solid', 'Plein'], ['outline', 'Contour'], ['ghost', 'Ghost']]} onPick={v => patch({ style: v })} /></Field>
        <Field label="Forme"><Pills value={p.shape} options={[['pill', 'Arrondi'], ['rect', 'Rectangle']]} onPick={v => patch({ shape: v })} /></Field>
        <Field label="Alignement"><Pills value={p.align} options={[['left', 'Gauche'], ['center', 'Centre'], ['right', 'Droite']]} onPick={v => patch({ align: v })} /></Field></>)}
      {b.type === 'spacer' && <Field label="Hauteur (px)"><input type="number" className={inp} value={p.height} onChange={e => patch({ height: parseInt(e.target.value) || 24 })} /></Field>}
      {b.type === 'columns2' && (p.items || []).map((it, i) => (
        <div key={i} className={`rounded-lg p-3 mb-3 ${light ? 'bg-gray-50' : 'bg-white/3'}`}>
          <p className={`text-[11px] font-bold mb-2 ${txtSub(light)}`}>Colonne {i + 1}</p>
          <Field label="Image URL"><input className={inp} value={it.image} onChange={e => { const items = [...p.items]; items[i] = { ...it, image: e.target.value }; patch({ items }); }} /></Field>
          <Field label="Titre"><input className={inp} value={it.title} onChange={e => { const items = [...p.items]; items[i] = { ...it, title: e.target.value }; patch({ items }); }} /></Field>
          <Field label="Texte"><input className={inp} value={it.text} onChange={e => { const items = [...p.items]; items[i] = { ...it, text: e.target.value }; patch({ items }); }} /></Field>
          <Field label="Lien"><input className={inp} value={it.url} onChange={e => { const items = [...p.items]; items[i] = { ...it, url: e.target.value }; patch({ items }); }} /></Field>
        </div>
      ))}
      {b.type === 'social' && Object.keys(p.links || {}).map(k => (
        <Field key={k} label={k.charAt(0).toUpperCase() + k.slice(1)}><input className={inp} value={p.links[k]} onChange={e => patch({ links: { ...p.links, [k]: e.target.value } })} placeholder="https://…" /></Field>
      ))}
      {b.type === 'html' && <Field label="Code HTML"><textarea rows={8} className={`${inp} font-mono text-xs`} value={p.code} onChange={e => patch({ code: e.target.value })} /></Field>}
      {b.type === 'footer' && <Field label="Texte du footer"><textarea rows={3} className={inp} value={p.text} onChange={e => patch({ text: e.target.value })} /></Field>}
      {b.type === 'dynamic' && (<><Field label="Type de contenu"><Pills value={p.kind} options={DYNAMIC_KINDS.map(k => [k.id, k.label])} onPick={v => patch({ kind: v })} /></Field>
        <Field label="Titre personnalisé (optionnel)"><input className={inp} value={p.title} onChange={e => patch({ title: e.target.value })} /></Field>
        <Field label="Nombre de contenus"><input type="number" min={2} max={8} className={inp} value={p.count} onChange={e => patch({ count: parseInt(e.target.value) || 4 })} /></Field></>)}
    </div>
  );
}
